import { Cart, freeGiftTrack } from '../alpine/cart';
import { cart as cartStore } from '../alpine/cartStore';
import { Product, ProductCard } from '../alpine/product';
import {
  cartRecommendations,
  cartRecommendation,
  // Recommendations
} from '../alpine/recommendations';

// Hero component
const xVideoIntersect = (intersection) => ({
  intersection,

  init() {
    if (!this.intersection) return;
    const videos = this.$el.querySelectorAll('video');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.play();
          } else {
            entry.target.pause();
          }
        });
      },
      { threshold: 0.65 }
    );

    // Observe each video when at least 50% comes into view
    videos.forEach((video) => {
      observer.observe(video);
    });
  },
});

const xAccordion = (initialState = null) => ({
  show: initialState !== null ? initialState : false,
  on() {
    this.show = true;
  },
  close() {
    this.show = false;
  },
  toggle() {
    this.show = !this.show;
  },
});

const xSearch = () => ({
  terms: '',
  results: '',
  getSearchResults() {
    if (!this.terms) {
      return;
    }

    // Fetch from Shopify section
    fetch(`/search/suggest?q=${this.terms}&section_id=search-results`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const results = new DOMParser().parseFromString(text, 'text/html').querySelector('#search-results')?.innerHTML;
        this.results = results ? results : null;
      })
      .catch((error) => {
        throw error;
      });
  },
  async open() {
    this.results = '';
    this.terms = '';
    await this.$nextTick();
    this.$refs.input.focus();
  },
});

const xCollectionFilter = (sort_options) => ({
  // Properties
  sort_by: null,
  query: '',
  sort_options,
  cache: [],
  showFilters: false,
  allowedQueryParams: ['sort_by', 'page'],
  // Getters
  get sort_label() {
    let label = this.sort_options?.find((o) => o[0] == this.sort_by)?.[1] || 'Options';
    switch (label) {
      case 'Best selling':
        label = 'Most popular';
        break;
      case 'Price, low to high':
        label = 'Price: Low to High';
        break;
      case 'Price, high to low':
        label = 'Price: High to Low';
        break;
    }

    return label;
  },
  get fetchUrl() {
    return `${window.location.pathname}?section_id=${this.$refs.grid.dataset.id}&${this.query}`;
  },
  // Methods
  sanitizeQueryParams(params) {
    const sanitizedParams = new URLSearchParams();

    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.') || this.allowedQueryParams.includes(key)) {
        sanitizedParams.append(key, value);
      }
    }

    return sanitizedParams;
  },
  updateQuery() {
    const formData = new FormData(this.$refs.form);
    const sanitizedParams = this.sanitizeQueryParams(new URLSearchParams(formData));
    // Update Query
    this.query = sanitizedParams.toString();
  },
  async render() {
    // First gather HTML from cache, if doesn't exist, fetch
    const results = this.cache[this.query] || (await this.fetchResults());
    // Replace Products HTML
    this.$refs.grid.innerHTML = results?.products || null;
    // Replace Pagination HTML
    this.$refs.pagination.innerHTML = results?.pagination || null;
  },
  async fetchResults() {
    try {
      const response = await fetch(this.fetchUrl);
      // If error, throw error
      if (!response.ok) {
        throw new Error(response.status);
      }
      const text = await response.text();
      // Const returned HTML
      const returnedHTML = new DOMParser().parseFromString(text, 'text/html');
      // Grab Products from the response
      const products = returnedHTML.querySelector('#collection-grid')?.innerHTML;
      // Update pagination
      const pagination = returnedHTML.querySelector('#pagination')?.innerHTML;

      const results = {
        products,
        pagination,
      };

      // Cache results
      this.cache[this.query] = results;
      // Return results
      return results;
    } catch (error) {
      throw error;
    }
  },
  // On init, grab from URL
  init() {
    const url = new URL(location);
    this.sort_by = url.searchParams.get('sort_by');
    const searchParams = this.sanitizeQueryParams(url.searchParams);
    this.query = searchParams.toString();

    // If filters are present in the url, show the filters
    if ((searchParams.has('page') && searchParams.size > 1) || (!searchParams.has('page') && searchParams.size > 0)) {
      // Unless it's mobile screen size!
      if (window.innerWidth > 768) {
        this.showFilters = true;
      }
    }

    // On change of query, replace Browser state
    this.$watch('query', async (value) => {
      // On mobile, scroll to content
      if (window.innerWidth < 768) {
        window.scrollTo({
          top: document.querySelector('#collection-grid').offsetTop,
        });
      }
      await this.render();
      history.replaceState({}, '', `${window.location.pathname}${this.query && '?'.concat(this.query)}`);
    });
  },
});

const xMultiRange = (max) => ({
  min: 0,
  max: parseInt(max),
  adjustLower($event) {
    this.min = $event.target.value;
    if ($event.target.value >= this.max) {
      const upper = parseInt($event.target.value) + 10;
      if (upper > this.max) {
        this.max = upper;
        return;
      }
      this.max = upper;
    }
  },
  adjustUpper($event) {
    this.max = $event.target.value;
    if ($event.target.value <= this.min) {
      const lower = $event.target.value - 10;
      if (lower < 0) {
        this.min = 0;
        return;
      }
      this.min = lower;
    }
  },
  init() {
    // If price ranges are present in the URL, then adjust min/max values
    const url = new URL(location);
    const min = url.searchParams.get('filter.v.price.gte');
    const max = url.searchParams.get('filter.v.price.lte');

    if (min) {
      this.min = min;
      this.$refs.lower.value = min;
    }
    if (max) {
      this.max = max;
      this.$refs.upper.value = max;
    }
  },
});

// Announcements
const xAnnouncements = () => ({
  current: 0,
  paused: false,
  colour: '',
  get items() {
    return this.$refs.announcement.querySelectorAll('li');
  },
  next() {
    this.current = this.current >= this.items.length - 1 ? 0 : this.current + 1;
  },
  prev() {
    this.current = this.current <= 0 ? this.items.length - 1 : this.current - 1;
  },
  init() {
    // On change of query, replace Browser state
    this.$watch('current', (value) => {
      const currentColour = this.items[value].dataset.colour;
      currentColour
        ? (this.$refs.outer.style.backgroundColor = currentColour)
        : (this.$refs.outer.style.backgroundColor = 'var(--green-lt)');
    });

    // Setup resizeObserver to check element's height
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        document.documentElement.style.setProperty('--announcement-height', `${entry.target.offsetHeight}px`);
      }
    });
    resizeObserver.observe(this.$refs.outer);

    // Set interval to change announcement
    setInterval(() => {
      if (this.paused) return;
      this.$nextTick(() => {
        this.next();
      });
    }, 5000);
  },
});

const xHeader = () => ({
  shop: false,
  explore: false,
  mobileMenu: false,
  scroll: 0,
  scrolledup: false,
  async setScrolled(e) {
    const direction = window.scrollY < 50 ? false : this.scroll < window.scrollY;
    direction
      ? document.documentElement.classList.add('scrolled')
      : document.documentElement.classList.remove('scrolled');
    this.scrolledup = direction;
    this.scroll = window.scrollY;
  },
  init() {
    this.$watch('explore', (value) => {
      if (value) this.shop = false;
    });
    this.$watch('shop', (value) => {
      if (value) this.explore = false;
    });
    // Close both explore and shop when search is opened
    Alpine.effect(() => {
      if (Alpine.store('searchOpen')) {
        this.explore = false;
        this.shop = false;
        this.mobileMenu = false;
      }
    });
  },
});

// Init components
document.addEventListener('alpine:init', () => {
  // Stores
  Alpine.store('searchOpen', false);
  Alpine.store('pdpQuickViewOpen', false);
  Alpine.store('cart', cartStore);

  // Set up a watcher for the cart items
  Alpine.data('cartRecommendations', cartRecommendations);
  Alpine.data('cartRecommendation', cartRecommendation);

  // Components
  Alpine.data('header', xHeader);
  Alpine.data('accordion', xAccordion);
  Alpine.data('product', Product);
  Alpine.data('productCard', ProductCard);
  Alpine.data('search', xSearch);
  Alpine.data('collectionFilter', xCollectionFilter);
  Alpine.data('multiRange', xMultiRange);
  Alpine.data('cart', Cart);
  // Alpine.data('recommendations', Recommendations);
  Alpine.data('announcement', xAnnouncements);
  Alpine.data('freeGiftTrack', freeGiftTrack);
  Alpine.data('videoIntersect', xVideoIntersect);
});
